import { DISMISS_MODAL, LOG_OUT_BIDDER } from './actions';
import { fetchBidderDataIfNeeded, getAuthToken, getBidderId } from './user';
import { getIsStripeCustomer, getStripeCreditCard } from './stripe';
import {
    getLAPaymentCards,
    postAddLACard,
    postAddStripeCard,
    postUpdateLACard,
    putAddStripeCard,
} from '@/redux/api/payment';
import { Address } from '@liveauctioneers/caterwaul-components/types/Address';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import paymentProviders from '@/enums/paymentProviders';

/* Action Types */
export const ADD_LA_PAYMENT_CARD_FAIL = 'la/ui/addLAPaymentCard/FAIL';
export const ADD_LA_PAYMENT_CARD_REQUEST = 'la/ui/addLAPaymentCard/REQUEST';
export const ADD_LA_PAYMENT_CARD_SUCCESS = 'la/ui/addLAPaymentCard/SUCCESS';

const UPDATE_LA_PAYMENT_CARD_FAIL = 'la/ui/updateLAPaymentCard/FAIL';
const UPDATE_LA_PAYMENT_CARD_REQUEST = 'la/ui/updateLAPaymentCard/REQUEST';
const UPDATE_LA_PAYMENT_CARD_SUCCESS = 'la/ui/updateLAPaymentCard/SUCCESS';

const LOAD_LA_PAYMENT_CARD_FAIL = 'la/ui/loadLAPaymentCard/FAIL';
const LOAD_LA_PAYMENT_CARD_REQUEST = 'la/ui/loadLAPaymentCard/REQUEST';
const LOAD_LA_PAYMENT_CARD_SUCCESS = 'la/ui/loadLAPaymentCard/SUCCESS';

// reducer
const DEFAULT_STATE = {
    error: false,
    errorMessage: null,
    laPaymentCards: [],
    submitted: false,
    success: false,
    updateSubmitted: false,
    updateSuccess: false,
};

export type State = typeof DEFAULT_STATE & {
    errorMessage: string | null | undefined;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case ADD_LA_PAYMENT_CARD_REQUEST:
        case LOAD_LA_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                error: false,
                errorMessage: null,
                submitted: true,
                success: false,
            };
        case ADD_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                laPaymentCards: [...state.laPaymentCards, action.payload],
                submitted: false,
                success: true,
            };
        case LOAD_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                laPaymentCards: action.payload,
                submitted: false,
                success: false,
            };
        case ADD_LA_PAYMENT_CARD_FAIL:
        case LOAD_LA_PAYMENT_CARD_FAIL:
        case UPDATE_LA_PAYMENT_CARD_FAIL:
            return {
                ...state,
                error: true,
                errorMessage: action.payload,
                submitted: false,
                success: false,
                updateSubmitted: false,
            };
        case UPDATE_LA_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                error: false,
                errorMessage: undefined,
                updateSubmitted: true,
                updateSuccess: false,
            };
        case UPDATE_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                error: false,
                errorMessage: undefined,
                laPaymentCards: [action.payload],
                updateSubmitted: false,
                updateSuccess: false,
            };
        case DISMISS_MODAL:
            return {
                ...state,
                error: false,
                errorMessage: null,
                submitted: false,
                success: false,
            };
        case LOG_OUT_BIDDER:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.creditCard;

const idSelector = (state: GlobalState, id) => id;

export const getUiAddCreditCard = createSelector(stateSelector, (state) => state);

/**
 * returns first matching card on file that is a defaultCard and matches providerId
 * @function laPaymentCardSelector
 * @category Credit Card Service
 * @param {GlobalState}, number
 */
const laPaymentCardSelector = createSelector(
    [stateSelector, idSelector],
    (state, providerId) =>
        state.laPaymentCards?.find((card) => card.defaultCard && card.providerId === providerId) || {}
);

export const getCreditCard = (state: any, paymentProvider: number) => {
    if (paymentProvider === paymentProviders.STRIPE) {
        return getStripeCreditCard(state);
    } else {
        return laPaymentCardSelector(state, paymentProvider);
    }
};

/**
 * selector returns all laPaymentCards from creditCard redux store
 * @function getAllCreditCards
 * @category Credit Card Service
 * @param {GlobalState}
 */
export const getAllCreditCards = (state) => {
    const payrixCard = getCreditCard(state, paymentProviders.PAYRIX);
    const stripeCard = getCreditCard(state, paymentProviders.STRIPE);
    const paysafeCard = getCreditCard(state, paymentProviders.PAYSAFE);

    const allCards = [];
    if (payrixCard.token) {
        allCards.push(payrixCard);
    }
    if (stripeCard.token) {
        allCards.push(stripeCard);
    }
    if (paysafeCard.token) {
        allCards.push(paysafeCard);
    }
    return allCards;
};

export const getShouldCollectBillingAddress = (state: any, paymentProvider: number) => {
    if (paymentProvider === paymentProviders.STRIPE) {
        return false;
    } else {
        const creditCard = laPaymentCardSelector(state, paymentProvider);
        return Boolean(creditCard.id) && !Boolean(creditCard.address.address1);
    }
};

/* ACTION CREATORS */
export type SubmitAddCreditCardParams = {
    address: Address;
    cardToken: string;
    cardholderName: any;
    providerId: number;
};
export const submitAddCreditCard = ({
    address,
    cardholderName,
    cardToken,
    providerId = paymentProviders.STRIPE,
}: SubmitAddCreditCardParams) => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const bidderId = getBidderId(state);

    // Ensure bidder profile is loaded
    if (!bidderId) {
        await dispatch(fetchBidderDataIfNeeded(authToken, true));
    }

    if (providerId === paymentProviders.STRIPE) {
        return await dispatch(addStripeCreditCard({ cardholderName, cardToken }));
    } else {
        return await dispatch(addLACreditCard({ address, cardholderName, cardToken, providerId }));
    }
};

type AddStripeCreditCardParams = {
    cardToken: string;
    cardholderName: string;
};
const addStripeCreditCard = ({ cardToken }: AddStripeCreditCardParams) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);
        const isStripeCustomer = getIsStripeCustomer(state);

        dispatch({
            payload: bidderId,
            type: ADD_LA_PAYMENT_CARD_REQUEST,
        });

        const apiCall = isStripeCustomer ? putAddStripeCard : postAddStripeCard;
        const response = await apiCall({ authToken, bidderId, cardToken, deployment });

        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: ADD_LA_PAYMENT_CARD_SUCCESS,
        });

        // Update bidder profile
        await dispatch(fetchBidderDataIfNeeded(authToken, true));
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: ADD_LA_PAYMENT_CARD_FAIL,
        });
        throw error;
    }
};

type AddLACreditCardParams = {
    address: Address;
    cardToken: string;
    cardholderName: any;
    providerId: number;
};
const addLACreditCard = ({ address, cardholderName, cardToken, providerId }: AddLACreditCardParams) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: bidderId,
            type: ADD_LA_PAYMENT_CARD_REQUEST,
        });

        // Add new payment method
        const response = await postAddLACard({
            authToken,
            bidderId,
            billingAddress: address,
            cardholderName: `${cardholderName.first} ${cardholderName.last}`,
            cardNonce: cardToken,
            deployment,
            providerId,
        });

        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: ADD_LA_PAYMENT_CARD_SUCCESS,
        });

        // Update user profile
        await dispatch(fetchBidderDataIfNeeded(authToken, true));
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: ADD_LA_PAYMENT_CARD_FAIL,
        });
        throw error;
    }
};

type UpdateLACreditCardParams = {
    billingAddress: Address;
    cardId: number;
};
export const updateLACreditCard = ({ billingAddress, cardId }: UpdateLACreditCardParams) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: bidderId,
            type: UPDATE_LA_PAYMENT_CARD_REQUEST,
        });

        // Send the request
        const response = await postUpdateLACard({
            authToken,
            bidderId,
            billingAddress,
            cardId,
            deployment,
        });
        dispatch({
            payload: response.payload,
            type: UPDATE_LA_PAYMENT_CARD_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: UPDATE_LA_PAYMENT_CARD_FAIL,
        });
        throw error;
    }
};

export const fetchLAPaymentCardIfNeeded = () => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        // if bidderId is less than 1 the bidder data hasnt loaded yet and we can return.
        if (bidderId < 1) {
            return Promise.resolve();
        }

        dispatch({
            payload: authToken,
            type: LOAD_LA_PAYMENT_CARD_REQUEST,
        });
        const response = await getLAPaymentCards({ authToken, bidderId, deployment });
        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: LOAD_LA_PAYMENT_CARD_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_LA_PAYMENT_CARD_FAIL,
        });
    }
};
